import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Heart, Mail, Phone, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useContent } from "@/lib/useContent";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/priorities", label: "Priorities" },
  { to: "/get-involved", label: "Get Involved" },
  { to: "/vote", label: "Vote" },
  { to: "/contact", label: "Contact" },
];

const socialIcon = { Facebook, Instagram, Twitter, YouTube: Youtube };

export const Header = () => {
  const { data: content } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const contact = content?.contact || {};
  const socials = contact.socials || [];

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* TOP UTILITY BAR — contact + social links, easily visible */}
      <div className="hero-gradient text-white" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-between text-xs sm:text-sm">
          <div className="hidden sm:flex items-center gap-5 text-blue-100">
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-email">
                <Mail className="w-3.5 h-3.5" /> {contact.email}
              </a>
            )}
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-phone">
                <Phone className="w-3.5 h-3.5" /> {contact.phone}
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 mx-auto sm:mx-0" data-testid="topbar-socials">
            <span className="hidden sm:inline text-blue-200 font-semibold mr-1">Follow:</span>
            {socials.map((s) => {
              const Icon = socialIcon[s.platform] || Mail;
              return (
                <a
                  key={s.platform}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.platform}
                  className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors"
                  data-testid={`topbar-social-${s.platform.toLowerCase()}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* MAIN NAV BAR */}
      <div className={`backdrop-blur-md bg-white/90 border-b border-slate-200/80 transition-all duration-300 ${scrolled ? "py-2 shadow-sm" : "py-3"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center group" data-testid="header-logo">
            <img
              src="/satish-logo.webp"
              alt="Elect Satish Chapagain — House of Delegates, District 7A"
              className={`w-auto transition-all duration-300 group-hover:scale-[1.03] ${scrolled ? "h-16 sm:h-20" : "h-24 sm:h-32"}`}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive ? "text-brightblue bg-sky" : "text-slate-700 hover:text-brightblue hover:bg-sky/60"}`
                }
                data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Button asChild size="lg" className="ml-3 bg-donate hover:bg-donate-hover text-white rounded-full font-bold text-base h-12 px-7 shadow-lg shadow-donate/30 hover:scale-105 transition-transform" data-testid="nav-donate-btn">
              <Link to="/donate"><Heart className="w-5 h-5 mr-2" /> Donate</Link>
            </Button>
          </nav>

          <button className="lg:hidden p-2 text-navy" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1" data-testid="mobile-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `block px-3 py-2.5 rounded-lg text-sm font-semibold ${isActive ? "text-brightblue bg-sky" : "text-slate-700"}`
              }
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
          <Button asChild size="lg" className="w-full mt-3 bg-donate hover:bg-donate-hover text-white rounded-full font-bold text-base h-13 py-6" data-testid="mobile-nav-donate-btn">
            <Link to="/donate"><Heart className="w-5 h-5 mr-2" /> Donate</Link>
          </Button>
        </div>
      )}
    </header>
  );
};
